'use client';

import { useState } from 'react';
import { Header } from '@/components/common/Header';
import { getSignupUrl } from '@/lib/signup';
import BriefingModal from '@/components/ui/BriefingModal';
import { useBriefingModal } from '@/hooks/useBriefingModal';
import type { BriefingModalContent } from '@/lib/types';

// ─── Types ──────────────────────────────────────────────────────────────────

type ConfidenceLevel = 'High' | 'Medium' | 'Corroborated' | 'Signal';

interface TradeRoute {
  id: string;
  product: string;
  route: string;
  margin: string;
  timeToExecute: string;
  confidence: ConfidenceLevel;
  updated: string;
  wholesale: string;
  retailRef: string;
  compliance: string;
  executionBreakdown: string;
}

// ─── Static Data ────────────────────────────────────────────────────────────

const TRADE_ROUTES: TradeRoute[] = [
  {
    id: 'eu-gmp-col-de',
    product: 'EU-GMP Flower',
    route: 'Colombia \u2192 Germany',
    margin: '~2.8x\u20133.4x',
    timeToExecute: '4\u20138 weeks',
    confidence: 'High',
    updated: '25m ago',
    wholesale: '$1,400\u2013$1,900/kg',
    retailRef: '$4,800\u2013$6,200/kg',
    compliance: 'EU-GMP required',
    executionBreakdown:
      'Approval: 2\u20134 weeks (INVIMA export + BfArM import) | Export Processing: 3\u20135 days | Transit: 5\u20137 days air freight | Import Clearance: 3\u20137 days German customs',
  },
  {
    id: 'dist-eu-il',
    product: 'Distillate (THC)',
    route: 'EU \u2192 Israel',
    margin: '~2.3x\u20132.8x',
    timeToExecute: '1\u20133 weeks',
    confidence: 'High',
    updated: '30m ago',
    wholesale: '$1,200\u2013$1,600/kg',
    retailRef: '$3,000\u2013$4,200/kg',
    compliance: 'GMP + Israeli MOH',
    executionBreakdown:
      'Approval: 5\u20138 days (EU export + MOH import license) | Export Processing: 2\u20133 days | Transit: 2\u20133 days | Import Clearance: 3\u20135 days',
  },
  {
    id: 'cbd-ch-uk',
    product: 'CBD Isolate 99%+',
    route: 'Switzerland \u2192 United Kingdom',
    margin: '~3.5x\u20133.7x',
    timeToExecute: '2\u20134 weeks',
    confidence: 'Medium',
    updated: '1h ago',
    wholesale: '$3,500\u2013$4,100/kg',
    retailRef: '$13,000\u2013$15,000/kg',
    compliance: 'EU-GMP + Novel Food',
    executionBreakdown:
      'Approval: 1\u20132 weeks (Swiss export + UK Home Office) | Export Processing: 2\u20134 days | Transit: 3\u20135 days | Import Clearance: 5\u20138 days post-Brexit',
  },
  {
    id: 'hemp-us-eu',
    product: 'Hemp Biomass (CBD)',
    route: 'USA \u2192 EU',
    margin: '~1.5x\u20132.0x',
    timeToExecute: '3\u20136 weeks',
    confidence: 'Corroborated',
    updated: '2h ago',
    wholesale: '$350\u2013$500/kg',
    retailRef: '$800\u2013$1,100/kg',
    compliance: 'USDA Hemp + EU Novel Food',
    executionBreakdown:
      'Approval: 1\u20132 weeks (USDA cert + EU import notification) | Export Processing: 3\u20135 days | Transit: 8\u201312 days sea freight | Import Clearance: 5\u20138 days',
  },
  {
    id: 'med-za-de',
    product: 'Medical Flower',
    route: 'South Africa \u2192 Germany',
    margin: '~3.2x\u20134.1x',
    timeToExecute: '6\u201312 weeks',
    confidence: 'Signal',
    updated: '4h ago',
    wholesale: '$800\u2013$1,200/kg',
    retailRef: '$3,500\u2013$5,000/kg',
    compliance: 'SAHPRA + EU-GMP (pending)',
    executionBreakdown:
      'Approval: 4\u20138 weeks (SAHPRA export + BfArM import) | Export Processing: 5\u20137 days | Transit: 5\u20137 days air freight | Import Clearance: 5\u201310 days',
  },
  {
    id: 'fso-uy-de',
    product: 'Full Spectrum Oil',
    route: 'Uruguay \u2192 Germany',
    margin: '~9.2x\u20139.4x',
    timeToExecute: '4\u20138 weeks',
    confidence: 'Medium',
    updated: '1h ago',
    wholesale: '$850\u2013$1,300/kg',
    retailRef: '$8,000\u2013$12,000/kg',
    compliance: 'IRCCA export + EU-GMP',
    executionBreakdown:
      'Approval: 2\u20134 weeks (IRCCA + BfArM) | Export Processing: 3\u20135 days | Transit: 7\u201310 days air/sea | Import Clearance: 5\u20138 days',
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function confidenceColor(level: ConfidenceLevel): string {
  const map: Record<ConfidenceLevel, string> = {
    High: 'text-success',
    Medium: 'text-warning',
    Corroborated: 'text-blue-400',
    Signal: 'text-blue-400',
  };
  return map[level];
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function routeToBrief(r: TradeRoute): BriefingModalContent {
  return {
    tag: 'TRADE INTELLIGENCE',
    title: `${r.product} — ${r.route}`,
    date: `Updated ${r.updated}`,
    byline: 'CID Trade Intelligence',
    body: `Margin range: ${r.margin}. Time to execute: ${r.timeToExecute}. Compliance: ${r.compliance}.`,
    signals: [
      { label: 'Margin', value: r.margin, tone: 'up' },
      { label: 'Wholesale', value: r.wholesale, tone: 'neutral' },
      { label: 'Retail Ref', value: r.retailRef, tone: 'neutral' },
      { label: 'Confidence', value: r.confidence, tone: r.confidence === 'High' ? 'up' : r.confidence === 'Medium' ? 'warn' : 'neutral' },
    ],
    actions: [
      { text: `Execute via the CID terminal: ${r.executionBreakdown}` },
      { text: 'Submit an RFQ to access verified suppliers operating on this corridor.' },
    ],
    cidTake: {
      what: `${r.product} on the ${r.route} corridor shows ${r.margin} margin with ${r.confidence.toLowerCase()} confidence.`,
      why: `Wholesale at ${r.wholesale} vs retail reference at ${r.retailRef}. Compliance requires ${r.compliance}.`,
      next: 'Request terminal access for live pricing and execution support.',
    },
    primaryCta: 'Request Access',
    primaryHref: getSignupUrl({ role: 'retailer' }),
    secondaryCta: 'Submit RFQ',
    secondaryHref: getSignupUrl({ role: 'retailer' }),
  };
}

function RouteRow({ route, onOpenBrief }: { route: TradeRoute; onOpenBrief: (b: BriefingModalContent) => void }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="border-b border-border cursor-pointer transition-colors hover:bg-surface"
      onClick={() => setExpanded((prev) => !prev)}
    >
      {/* Signal row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 pt-3.5 pb-1">
        <span className="text-sm font-semibold text-foreground min-w-[200px]">{route.product}</span>
        <span className="text-[13px] text-text-secondary">{route.route}</span>
        <span className="text-[12px] text-text-secondary">
          Margin: <span className={`font-semibold ${confidenceColor(route.confidence)}`}>{route.margin}</span>
        </span>
        <span className="text-[12px] text-text-secondary">
          Time to Execute: <span className="font-semibold">{route.timeToExecute}</span>
        </span>
        <span className="text-[12px] text-text-secondary">
          Confidence: <span className={`font-semibold ${confidenceColor(route.confidence)}`}>{route.confidence}</span>
        </span>
        <span className="text-[12px] text-text-secondary">
          Updated: <span className="font-semibold">{route.updated}</span>
        </span>
      </div>

      {/* Detail row (always visible) */}
      <div className="px-3 pb-3 text-[12px] text-muted-foreground">
        Wholesale: {route.wholesale} | Retail Ref: {route.retailRef} | Compliance: {route.compliance}
      </div>

      {/* Expanded execution breakdown */}
      {expanded && (
        <div className="px-3 pb-4 pt-2 bg-muted/50 border-t border-border text-[12px] text-muted-foreground leading-relaxed">
          <span className="font-semibold text-foreground">Execution Breakdown:</span>
          <br />
          {route.executionBreakdown}
          <button
            onClick={(e) => { e.stopPropagation(); onOpenBrief(routeToBrief(route)); }}
            className="mt-3 px-3 py-1.5 bg-primary text-primary-foreground rounded-[3px] text-[11px] font-semibold hover:bg-secondary transition-colors"
          >
            View Full Brief
          </button>
        </div>
      )}
    </div>
  );
}

function CidTakeSidebar() {
  return (
    <aside className="w-full lg:w-[300px] flex-shrink-0 lg:sticky lg:top-[72px]">
      <div className="bg-surface border border-border rounded-[10px] p-5">
        <h3 className="text-[15px] font-bold text-primary mb-4">CID TAKE &mdash; Trade Intelligence</h3>

        {/* What / Why / What to do */}
        <div className="bg-primary/[0.06] border border-primary/15 rounded-[10px] p-5 mb-5 text-[13px] leading-relaxed text-foreground space-y-2">
          <div>
            <strong className="text-primary">WHAT IS HAPPENING:</strong>{' '}
            6 executable trade corridors are live. Colombia corridors show strongest
            margins with EU-GMP Flower and Full Spectrum Oil both demonstrating
            significant spread above wholesale reference.
          </div>
          <div>
            <strong className="text-primary">WHY IT MATTERS:</strong>{' '}
            High-confidence routes with verified compliance are rare. Most margins
            above 5x will not persist &mdash; new supply and regulatory shifts will
            compress them within 60&ndash;90 days.
          </div>
          <div>
            <strong className="text-primary">WHAT TO DO:</strong>{' '}
            Prioritize High-confidence corridors first. Colombia&rarr;Germany EU-GMP
            Flower and EU&rarr;Israel Distillate are the strongest risk-adjusted
            plays right now.
          </div>
        </div>

        <p className="text-[12px] text-text-secondary mb-4">
          Trade Intelligence shows executable market conditions across global
          cannabis trade routes. Each row represents a real corridor with verified
          pricing, timing, and compliance data.
        </p>

        <SidebarSection title="How to Read This Table">
          <ul className="list-disc pl-3.5 space-y-1 text-[12px] text-text-secondary">
            <li><strong>Margin</strong> &mdash; Approximate spread between wholesale acquisition and retail reference price in the destination market.</li>
            <li><strong>Time to Execute</strong> &mdash; Full cycle: approval, export processing, transit, and import clearance. Not just shipping.</li>
            <li><strong>Confidence</strong> &mdash; High = multiple corroborated sources. Medium = 2+ sources. Corroborated = verified but limited. Signal = early indicator only.</li>
            <li><strong>Updated</strong> &mdash; Time since last data refresh for that route.</li>
          </ul>
        </SidebarSection>

        <SidebarSection title="Forward View">
          <p className="text-[12px] text-text-secondary">
            Rows reflect current executable conditions, not historical averages.
            Pricing and timing update as regulatory, supply, and logistics
            conditions shift.
          </p>
        </SidebarSection>

        <SidebarSection title="Expand Rows">
          <p className="text-[12px] text-text-secondary">
            Click any row to see execution breakdown: approval timeline, export
            processing, transit duration, and import clearance detail.
          </p>
        </SidebarSection>

        <SidebarSection title="What This Is Not">
          <p className="text-[12px] text-text-secondary">
            This is not a deal board or opportunity listing. Trade Intelligence
            reflects market structure &mdash; what it takes to move product from
            origin to destination under real compliance and logistics constraints.
          </p>
        </SidebarSection>
      </div>
    </aside>
  );
}

function SidebarSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <h4 className="text-[12px] font-semibold text-foreground uppercase tracking-wide mb-1">{title}</h4>
      {children}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export default function TradeIntelligencePage() {
  const { isOpen, content, openBrief, closeBrief } = useBriefingModal();

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main>
        {/* Page header */}
        <div className="max-w-7xl mx-auto px-6 pt-8 pb-2">
          <h1 className="text-[28px] font-bold text-foreground">Trade Intelligence</h1>
          <div className="text-[13px] text-primary tracking-wide mt-1">
            Buy &rarr; Approve &rarr; Export &rarr; Transit &rarr; Import &rarr; Sell
          </div>
          <div className="text-[12px] text-muted-foreground mt-1">
            Execution-grade market view based on real trade conditions
          </div>
        </div>

        {/* Layout: table + sidebar */}
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col lg:flex-row gap-7 items-start">
          {/* Main table */}
          <div className="flex-1 min-w-0">
            <div className="w-full">
              {TRADE_ROUTES.map((r) => (
                <RouteRow key={r.id} route={r} onOpenBrief={openBrief} />
              ))}
            </div>

            {/* Gate CTA below table */}
            <div className="bg-surface border border-border rounded-[10px] p-8 text-center mt-8">
              <h3 className="text-lg font-bold text-foreground mb-2">Access Full Trade Intelligence</h3>
              <p className="text-[13px] text-muted-foreground mb-5 max-w-md mx-auto">
                Live pricing, compliance verification, and execution support for
                all active corridors. Free for qualified buyers.
              </p>
              <a
                href={getSignupUrl({ role: 'retailer' })}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-7 py-2.5 text-[13px] font-bold bg-primary text-primary-foreground rounded cursor-pointer hover:bg-secondary transition-colors"
              >
                Request Access
              </a>
            </div>
          </div>

          {/* Sidebar */}
          <CidTakeSidebar />
        </div>
      </main>

      <BriefingModal isOpen={isOpen} content={content} onClose={closeBrief} />
    </div>
  );
}
